import { Trans, t } from '@lingui/macro';
import { MoreVertical, MessageCircle, UserX, Crown } from "lucide-react";
import { useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { Room } from "../../models/Room";
import { User } from "../../models/User";
import { UserBlock } from "../../models/UserBlock";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import ProfileImg from "./ProfileImg";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";

interface UserPopOverProps {
  user: User;
  content: React.ReactNode;
  room?: Room;
  isCreator?: boolean;
  canKick?: boolean;
  onKick?: (user: User) => void;
}

export const UserPopOver: React.FC<UserPopOverProps> = ({
  user,
  content,
  room,
  isCreator = false,
  canKick = false,
  onKick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showKickDialog, setShowKickDialog] = useState(false);
  const history = useHistory();
  const { toast } = useToast();

  const isMe = My.user?.id === user.id;

  async function gotoOneToOne() {
    setIsOpen(false);
    let existingRoom = Array.from(My.Rooms.values()).find(r => r.isOneToOne && r.userIds.includes(user.id) && r.userIds.includes(My.user.id));
    if (!existingRoom) {
      existingRoom = new Room(`${user.name}${Util.OneToOneDelimiter}${My.user.name}`, My.user.id);
      existingRoom.isOneToOne = true;
      existingRoom.users = [My.user, user];
      existingRoom.userIds = [My.user.id, user.id];
      await Fire.saveRoom(existingRoom);
    }

    setTimeout(() => {
      history.replace(`/room/${existingRoom!.id}`);
    }, 100);
  }

  async function handleBlock() {
    if (My.UserBlocks.get(user.id) == null) {
      let block = new UserBlock(user, My.user.id);
      My.UserBlocks.set(block.blockedUser.id, block);
      await Fire.saveUserBlock(block);
      toast({
        title: t`${user.name} has been blocked. Reload to apply.`,
        duration: 3000,
      });
    }
    setShowBlockDialog(false);
    setIsOpen(false);
  }

  function handleKick() {
    onKick?.(user);
    setShowKickDialog(false);
    setIsOpen(false);
  }

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <span className="cursor-pointer">
            {content}
          </span>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0 rounded-xl shadow-xl border-slate-200" align="start">
          {/* 유저 정보 헤더 - Centered Style */}
          <div className="flex flex-col items-center p-6 border-b border-slate-100 bg-slate-50/30">
            <Avatar className="w-20 h-20 shadow-md border-4 border-white mb-3">
              <AvatarImage src={user?.photoUrl ?? undefined} />
              <AvatarFallback className="text-2xl h-full w-full flex items-center justify-center bg-slate-200 text-slate-500">
                {user.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>

            <div className="flex items-center gap-1.5 mb-1">
              <span className="font-bold text-lg text-slate-900">{user.name}</span>
              {isMe && <Badge variant="secondary" className="text-[10px] h-5">You</Badge>}
              {isCreator && (
                <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px] h-5 gap-0.5 border-orange-200">
                  <Crown className="w-3 h-3" />
                  <Trans>Owner</Trans>
                </Badge>
              )}
            </div>

            <a href={`mailto:${user.email}`} className="text-sm text-slate-500 hover:text-indigo-600 hover:underline transition-colors mb-3">
              {user.email}
            </a>

            {user.description && (
              <div className="w-full mt-2 text-sm text-slate-600 bg-white border border-slate-200 px-3 py-2.5 rounded-lg text-center italic">
                "{user.description}"
              </div>
            )}
          </div>

          {/* 액션 버튼들 */}
          <div className="p-2 flex flex-col gap-1 bg-white">
            {!isMe && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-10 px-4 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 font-medium transition-colors"
                onClick={gotoOneToOne}
                data-cy="one2one"
              >
                <MessageCircle className="w-4 h-4" />
                <Trans>Open One-to-one</Trans>
              </Button>
            )}

            {!isMe && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-10 px-4 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                onClick={() => setShowBlockDialog(true)}
              >
                <UserX className="w-4 h-4" />
                <Trans>Block {user.name}</Trans>
              </Button>
            )}

            {canKick && !isMe && !isCreator && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-10 px-4 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
                onClick={() => setShowKickDialog(true)}
              >
                <UserX className="w-4 h-4" />
                <Trans>Kick out</Trans>
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* 차단 확인 다이얼로그 */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Block ${user.name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`Messages created by this person will be hidden for you. We do not inform the person that you have blocked them.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={handleBlock} className="bg-red-500 hover:bg-red-600">
              {t`Block`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 강퇴 확인 다이얼로그 */}
      <AlertDialog open={showKickDialog} onOpenChange={setShowKickDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t`Kick out ${user.name}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {t`This member will be removed from the room.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
            <AlertDialogAction onClick={handleKick} className="bg-red-500 hover:bg-red-600">
              {t`Kick out`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default UserPopOver;
